import { Directive, HostListener, Input } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Directive({
  selector: '[downloadFile]'
})
export class DownloadFileDirective {
  constructor(private readonly httpClient: HttpClient) {}

  private downloadUrl: string;
  private fileNameSuggested: string = "file";

  @Input('downloadFile')
  public set url(url: string) {
    this.downloadUrl = url;
  };

  @Input('fileName')
  public set fileName(filename: string) {
    this.fileNameSuggested = filename;
  };

  @HostListener('click')
  public async onClick(): Promise<void> {

    // Download the document as a blob
    const response = await this.httpClient.get(
      this.downloadUrl,
      { responseType: 'blob', observe: 'response' }
    ).toPromise();

    // Create a URL for the blob
    const url = URL.createObjectURL(response.body);

    // Create an anchor element to "point" to it
    const anchor = document.createElement('a');
    anchor.href = url;

    // Get the suggested filename for the file from the response headers
    // SECURITY: sanitize before assigning to anchor.download to prevent path traversal / header injection (R2-4)
    const rawFilename = this.getFilenameFromHeaders(response.headers) || this.fileNameSuggested;
    anchor.download = this.sanitizeDownloadFilename(rawFilename);

    // Simulate a click on our anchor element
    anchor.click();

    // Discard the object data
    URL.revokeObjectURL(url);
  }

  /** SECURITY: Strip path traversal characters, null bytes, and control chars from filenames.
   *  Allow only safe characters; collapse anything else to underscore (R2-4). */
  private sanitizeDownloadFilename(name: string): string {
    // Remove path separators, null bytes, and all control characters (0x00-0x1f, 0x7f)
    return name
      .replace(/\.\./g, '_')               // no directory traversal
      .replace(/[\/\\]/g, '_')             // no path separators
      .replace(/[\x00-\x1f\x7f]/g, '_')   // no control/null bytes
      .replace(/[^A-Za-z0-9.\-_ ]/g, '_') // allow only safe chars
      || 'file';                           // never return empty string
  }

  private getFilenameFromHeaders(headers: HttpHeaders) {
    // The content-disposition header should include a suggested filename for the file
    const contentDisposition = headers.get('Content-Disposition');
    if (!contentDisposition) {
      return null;
    }

    /* StackOverflow is full of RegEx-es for parsing the content-disposition header,
    * but that's overkill for my purposes, since I have a known back-end with
    * predictable behaviour. I can afford to assume that the content-disposition
    * header looks like the example in the docs
    * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Disposition
    *
    * In other words, it'll be something like this:
    *    Content-Disposition: attachment; filename="filename.ext"
    *
    * I probably should allow for single and double quotes (or no quotes) around
    * the filename. I don't need to worry about character-encoding since all of
    * the filenames I generate on the server side should be vanilla ASCII.
    */

    const leadIn = 'filename=';
    const start = contentDisposition.search(leadIn);
    if (start < 0) {
      return null;
    }

    // Get the 'value' after the filename= part (which may be enclosed in quotes)
    const value = contentDisposition.substring(start + leadIn.length).trim();
    if (value.length === 0) {
      return null;
    }

    // If it's not quoted, we can return the whole thing
    const firstCharacter = value[0];
    if (firstCharacter !== '\"' && firstCharacter !== '\'') {
      return value;
    }

    // If it's quoted, it must have a matching end-quote
    if (value.length < 2) {
      return null;
    }

    // The end-quote must match the opening quote
    const lastCharacter = value[value.length - 1];
    if (lastCharacter !== firstCharacter) {
      return null;
    }

    // Return the content of the quotes
    return value.substring(1, value.length - 1);
  }
}